import { useId, useState } from "react";
import {
	Box,
	Button,
	Collapse,
	Link,
	Paper,
	Slide,
	Stack,
	Switch,
	Typography,
	alpha,
} from "@mui/material";
import { CookieIcon } from "@phosphor-icons/react";
import { t } from "i18next";
import { useCookiePreferencesStore } from "./cookie-preferences-store";
import { useMatomo } from "@/lib/matomo";
import LOCAL_STORAGE_KEYS from "@/constants/local-storage-keys";
import { ICON_SIZES } from "@/constants/icons";

function hasStoredDecision() {
	try {
		return localStorage.getItem(LOCAL_STORAGE_KEYS.MATOMO_CONSENT) !== null;
	} catch {
		return false;
	}
}

interface PreferenceRowProps {
	title: string;
	description: string;
	control: React.ReactNode;
	divider?: boolean;
}

function PreferenceRow({
	title,
	description,
	control,
	divider,
}: PreferenceRowProps) {
	return (
		<Stack
			direction="row"
			alignItems="center"
			spacing={1.5}
			sx={{
				px: 1.75,
				py: 1.5,
				...(divider && { borderBottom: 1, borderColor: "divider" }),
			}}
		>
			<Box flex={1} minWidth={0}>
				<Typography variant="subtitle2">{title}</Typography>
				<Typography variant="caption" color="text.secondary" component="p">
					{description}
				</Typography>
			</Box>
			{control}
		</Stack>
	);
}

/**
 * Asks for analytics (Matomo) consent on first visit, and again whenever the
 * user picks "Cookie preferences" in the user menu. Decline and Accept carry the
 * same weight, and the analytics switch starts off unless consent was given:
 * a pre-ticked box is not valid consent under the GDPR.
 */
export function CookieConsentBanner() {
	const { hasConsent, giveConsent, removeConsent } = useMatomo();
	const reopened = useCookiePreferencesStore((state) => state.reopened);
	const closeCookiePreferences = useCookiePreferencesStore(
		(state) => state.closeCookiePreferences
	);
	const [decided, setDecided] = useState(hasStoredDecision);
	const [customizing, setCustomizing] = useState(false);
	const [analytics, setAnalytics] = useState(hasConsent);
	const [wasReopened, setWasReopened] = useState(reopened);
	const titleId = useId();

	// Reopening from the user menu jumps straight to the switches, set to the
	// choice currently in effect.
	if (reopened !== wasReopened) {
		setWasReopened(reopened);
		if (reopened) {
			setCustomizing(true);
			setAnalytics(hasConsent);
		}
	}

	const open = !decided || reopened;

	function finish(allowAnalytics: boolean) {
		if (allowAnalytics) {
			giveConsent();
		} else {
			removeConsent();
		}
		setDecided(true);
		setCustomizing(false);
		closeCookiePreferences();
	}

	return (
		<Slide direction="up" in={open} mountOnEnter unmountOnExit>
			<Paper
				role="dialog"
				aria-labelledby={titleId}
				elevation={12}
				sx={{
					position: "fixed",
					bottom: { xs: 16, sm: 24 },
					right: { xs: 16, sm: 24 },
					left: { xs: 16, sm: "auto" },
					width: { sm: 420 },
					p: 2.5,
					borderRadius: 1.75,
					border: 1,
					borderColor: "divider",
					zIndex: (theme) => theme.zIndex.snackbar,
				}}
			>
				<Stack spacing={2}>
					<Stack direction="row" spacing={1.75} alignItems="flex-start">
						<Box
							sx={(theme) => ({
								width: 40,
								height: 40,
								flexShrink: 0,
								borderRadius: 1.25,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								bgcolor: alpha(theme.palette.primary.main, 0.12),
								color: "primary.dark",
								...theme.applyStyles("dark", { color: "primary.light" }),
							})}
						>
							<CookieIcon size={ICON_SIZES.large} aria-hidden />
						</Box>
						<Stack spacing={0.75}>
							<Typography id={titleId} variant="subtitle1" fontWeight={600}>
								{t("Cookie preferences")}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{t(
									"We use analytics cookies to see how the app is used, so we can keep improving it."
								)}{" "}
								<Link
									href="/privacy-policy"
									target="_blank"
									rel="noopener noreferrer"
								>
									{t("Privacy Policy")}
								</Link>
							</Typography>
						</Stack>
					</Stack>

					<Collapse in={customizing} unmountOnExit>
						<Box sx={{ border: 1, borderColor: "divider", borderRadius: 1.25 }}>
							<PreferenceRow
								divider
								title={t("Essential")}
								description={t(
									"Needed to sign you in and keep the app working. Always on."
								)}
								control={
									<Switch
										checked
										disabled
										slotProps={{
											input: {
												role: "switch",
												"aria-label": t("Essential cookies"),
											},
										}}
									/>
								}
							/>
							<PreferenceRow
								title={t("Analytics")}
								description={t(
									"Usage statistics (Matomo) that help us find what to improve."
								)}
								control={
									<Switch
										checked={analytics}
										onChange={(event) => setAnalytics(event.target.checked)}
										slotProps={{
											input: {
												role: "switch",
												"aria-label": t("Analytics cookies"),
											},
										}}
									/>
								}
							/>
						</Box>
					</Collapse>

					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						flexWrap="wrap"
						useFlexGap
					>
						{!customizing && (
							<Button variant="text" onClick={() => setCustomizing(true)}>
								{t("Customize")}
							</Button>
						)}
						<Box flex={1} />
						<Button variant="outlined" onClick={() => finish(false)}>
							{t("Decline")}
						</Button>
						{customizing ? (
							<Button variant="contained" onClick={() => finish(analytics)}>
								{t("Save choices")}
							</Button>
						) : (
							<Button variant="contained" onClick={() => finish(true)}>
								{t("Accept")}
							</Button>
						)}
					</Stack>

					<Typography
						variant="caption"
						color="text.secondary"
						textAlign="center"
						sx={{ mt: -0.5 }}
					>
						{t(
							"You can change this later via Cookie preferences in the user menu."
						)}
					</Typography>
				</Stack>
			</Paper>
		</Slide>
	);
}
